import { Activity, ArrowRight, Download, Eye, History, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { ChangeValue, fieldLabelFor } from "@/components/shared/activity-log";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { FilterRow } from "@/components/shared/filter-bar";
import { FilterPanel, selectedLabels } from "@/components/shared/filter-panel";
import { FilterToggleGroup } from "@/components/shared/filter-toggle-group";
import { CursorPagination } from "@/components/shared/pagination";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import type { FieldChange } from "@/hooks/use-audit-log";
import {
  GLOBAL_AUDIT_PAGE_SIZE,
  type GlobalAuditEntry,
  useAuditEntityTypes,
  useExportGlobalAuditLog,
  useGlobalAuditLog,
} from "@/hooks/use-audit-log";
import { useEntityOptionLabels } from "@/hooks/use-entity-options";
import { actorLabel } from "@/lib/audit-actor-label";
import { formatEffectiveDate, formatRelative } from "@/lib/dates";
import { cn } from "@/lib/utils";

// The audit diff arrives as `unknown` JSON from the server. Narrow each element
// to a FieldChange rather than blanket-casting the array — a malformed row then
// yields fewer rendered changes instead of a runtime crash on `.field`.
function toFieldChanges(diff: unknown): FieldChange[] {
  if (!Array.isArray(diff)) return [];
  return diff.filter(
    (c): c is FieldChange =>
      typeof c === "object" && c !== null && "field" in c && typeof c.field === "string",
  );
}

const ACTION_OPTIONS = [
  { value: "created", label: "Created" },
  { value: "updated", label: "Updated" },
  { value: "deleted", label: "Deleted" },
];

/** "skill_category" → "Skill category". */
function entityTypeLabel(entityType: string): string {
  const words = entityType.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Local start-of-day / end-of-day instants from a date input's YYYY-MM-DD. */
function dayStartIso(day: string): string {
  return day ? new Date(`${day}T00:00:00`).toISOString() : "";
}
function dayEndIso(day: string): string {
  return day ? new Date(`${day}T23:59:59.999`).toISOString() : "";
}

export function AuditLogTab() {
  const [actions, setActions] = useState<string[]>([]);
  const [entityTypes, setEntityTypes] = useState<string[]>([]);
  const [actorUserId, setActorUserId] = useState<string | null>(null);
  const [fromDay, setFromDay] = useState("");
  const [toDay, setToDay] = useState("");
  const [page, setPage] = useState(1);
  const [pageRequest, setPageRequest] = useState<{
    cursor: string | null;
    direction: "next" | "prev";
  }>({ cursor: null, direction: "next" });

  const { data: typeList } = useAuditEntityTypes();
  const entityTypeOptions = (typeList ?? []).map((t) => ({
    value: t,
    label: entityTypeLabel(t),
  }));

  const filters = {
    entityTypes,
    actions,
    actorUserId: actorUserId ?? "",
    from: dayStartIso(fromDay),
    to: dayEndIso(toDay),
  };
  const { data, isLoading, isError, refetch } = useGlobalAuditLog(filters, pageRequest);
  const exportAuditLog = useExportGlobalAuditLog();

  const total = data?.total ?? 0;
  const entries = data?.data ?? [];

  function resetPage() {
    setPage(1);
    setPageRequest({ cursor: null, direction: "next" });
  }

  const actorLabels = useEntityOptionLabels("users", actorUserId ? [actorUserId] : []);
  const actorName = actorLabels.data?.[0]?.label;

  const filterSummary = [
    ...(actions.length ? [`Action: ${selectedLabels(ACTION_OPTIONS, actions)}`] : []),
    ...(entityTypes.length ? [`Entity: ${selectedLabels(entityTypeOptions, entityTypes)}`] : []),
    ...(actorUserId ? [`By: ${actorName ?? "…"}`] : []),
    ...(fromDay ? [`From: ${fromDay}`] : []),
    ...(toDay ? [`To: ${toDay}`] : []),
  ];
  const activeCount =
    actions.length +
    entityTypes.length +
    (actorUserId ? 1 : 0) +
    (fromDay ? 1 : 0) +
    (toDay ? 1 : 0);

  return (
    <div className="space-y-4">
      <FilterPanel
        activeCount={activeCount}
        summary={filterSummary}
        canReset={activeCount > 0}
        onReset={() => {
          setActions([]);
          setEntityTypes([]);
          setActorUserId(null);
          setFromDay("");
          setToDay("");
          resetPage();
        }}
      >
        <FilterRow label="Action">
          <FilterToggleGroup
            options={ACTION_OPTIONS}
            value={actions}
            onChange={(v) => {
              setActions(v);
              resetPage();
            }}
          />
        </FilterRow>
        <FilterRow label="Entity">
          <FilterToggleGroup
            options={entityTypeOptions}
            value={entityTypes}
            onChange={(v) => {
              setEntityTypes(v);
              resetPage();
            }}
          />
        </FilterRow>
        <FilterRow label="Changed by">
          <EntityCombobox
            entity="users"
            value={actorUserId}
            onChange={(v) => {
              setActorUserId(v);
              resetPage();
            }}
            includeNone
            noneLabel="Anyone"
            placeholder="Anyone"
            className="w-[220px]"
          />
        </FilterRow>
        <FilterRow label="Date range">
          <Input
            type="date"
            aria-label="From date"
            value={fromDay}
            max={toDay || undefined}
            onChange={(e) => {
              setFromDay(e.target.value);
              resetPage();
            }}
            className="w-[160px]"
          />
          <span className="text-xs text-muted-foreground">to</span>
          <Input
            type="date"
            aria-label="To date"
            value={toDay}
            min={fromDay || undefined}
            onChange={(e) => {
              setToDay(e.target.value);
              resetPage();
            }}
            className="w-[160px]"
          />
        </FilterRow>
      </FilterPanel>

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-2 space-y-0">
          <div className="space-y-1.5">
            <CardTitle>Audit log</CardTitle>
            <CardDescription>
              Every change made across the app — who changed what, and when.
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => exportAuditLog.mutate(filters)}
            disabled={exportAuditLog.isPending || isLoading || total === 0}
          >
            <Download className="mr-2 h-4 w-4" />
            {exportAuditLog.isPending ? "Exporting…" : "Export CSV"}
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : isError ? (
            <ErrorState message="Failed to load the audit log" onRetry={() => refetch()} />
          ) : entries.length === 0 ? (
            <EmptyState
              icon={History}
              title="No changes found"
              description={
                activeCount > 0
                  ? "No audit entries match the current filters."
                  : "Changes will appear here as people use the app."
              }
            />
          ) : (
            <ol className="space-y-4">
              {entries.map((entry) => (
                <AuditEntryRow key={entry.id} entry={entry} />
              ))}
            </ol>
          )}

          <CursorPagination
            className="mt-6"
            total={total}
            page={page}
            limit={GLOBAL_AUDIT_PAGE_SIZE}
            hasNext={data?.hasNext ?? false}
            hasPrev={data?.hasPrev ?? false}
            loading={isLoading}
            itemLabel="changes"
            onNext={() => {
              if (!data?.nextCursor) return;
              setPage((p) => p + 1);
              setPageRequest({ cursor: data.nextCursor, direction: "next" });
            }}
            onPrev={() => {
              if (!data?.prevCursor) return;
              setPage((p) => Math.max(1, p - 1));
              setPageRequest({ cursor: data.prevCursor, direction: "prev" });
            }}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function AuditEntryRow({ entry }: { entry: GlobalAuditEntry }) {
  // An admin starting/stopping "view as" someone: no diff, no entity badge —
  // the sentence is the whole record.
  if (entry.entityType === "impersonation") {
    return (
      <li className="flex gap-3 border-b pb-4 last:border-b-0 last:pb-0">
        <div className="mt-0.5 shrink-0 text-info">
          <Eye className="h-4 w-4" />
        </div>
        <p className="flex-1 text-sm">
          <span className="font-medium">{entry.actorName ?? "System"}</span>{" "}
          <span className="text-muted-foreground">
            {entry.action === "created" ? "started viewing as" : "stopped viewing as"}
          </span>{" "}
          <span className="font-medium">{entry.entityName ?? "a deleted account"}</span>{" "}
          <span
            className="whitespace-nowrap text-muted-foreground"
            title={new Date(entry.createdAt).toLocaleString()}
          >
            {formatRelative(new Date(entry.createdAt))}
          </span>
        </p>
      </li>
    );
  }

  const Icon = entry.action === "created" ? Plus : entry.action === "deleted" ? Trash2 : Activity;
  const tone =
    entry.action === "created"
      ? "text-success"
      : entry.action === "deleted"
        ? "text-destructive"
        : "text-info";

  return (
    <li className="flex gap-3 border-b pb-4 last:border-b-0 last:pb-0">
      <div className={cn("mt-0.5 shrink-0", tone)}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 space-y-1">
        <p className="text-sm">
          <span className="font-medium">{actorLabel(entry)}</span>{" "}
          <span className="text-muted-foreground">{entry.action}</span>{" "}
          <Badge variant="outline" className="align-middle">
            {entityTypeLabel(entry.entityType)}
          </Badge>{" "}
          {entry.entityName && <span className="font-medium">{entry.entityName}</span>}
          {entry.effectiveDate && (
            <span className="text-muted-foreground">
              {" "}
              (effective {formatEffectiveDate(entry.effectiveDate)})
            </span>
          )}{" "}
          <span
            className="whitespace-nowrap text-muted-foreground"
            title={new Date(entry.createdAt).toLocaleString()}
          >
            {formatRelative(new Date(entry.createdAt))}
          </span>
        </p>
        {entry.action === "updated" && toFieldChanges(entry.diff).length > 0 && (
          <ul className="space-y-0.5 text-xs">
            {toFieldChanges(entry.diff).map((change) => (
              <li key={change.field} className="text-muted-foreground">
                <span className="font-medium text-foreground">{fieldLabelFor(change.field)}</span>
                {": "}
                <ChangeValue value={change.before} field={change.field} />
                <ArrowRight className="mx-1 inline h-3 w-3" />
                <ChangeValue value={change.after} field={change.field} />
              </li>
            ))}
          </ul>
        )}
        {entry.changeReasonName && (
          <p className="text-xs text-muted-foreground">
            Reason: <span className="font-medium text-foreground">{entry.changeReasonName}</span>
          </p>
        )}
      </div>
    </li>
  );
}
